(() => {
  const instances = new Set();
  const timeoutMs = 12000;
  let epoch = 0;
  let pending = true;
  let awaitingCart = false;
  let expectedCart = null;
  let expectedSignature = null;
  let candidate = null;
  let accepted = null;
  let timeoutId;
  let timedOut = false;
  let mutationsInFlight = 0;
  let mutationVersion = 0;
  const freshRawSignatures = new Set();
  const xhrRequests = new WeakMap();
  const handledRequests = new WeakSet();

  const normalise = (value) => {
    if (Array.isArray(value)) return value.map(normalise);
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.keys(value).sort().map((key) => [key, normalise(value[key])]));
    }
    return value;
  };

  const properties = (value) => {
    if (!value) return {};
    if (Array.isArray(value)) {
      if (value.length === 0) return {};
      if (value.every((entry) => Array.isArray(entry) && entry.length === 2)) {
        return normalise(Object.fromEntries(value));
      }
    }
    return normalise(value);
  };

  const signature = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return null;
    const originalTotal = Number(cart.original_total_price);
    if (!Number.isSafeInteger(originalTotal) || originalTotal < 0) return null;
    const lines = cart.items.map((item) => {
      const id = item.variant_id ?? item.id;
      const quantity = Number(item.quantity);
      if (id == null || !Number.isSafeInteger(quantity) || quantity < 1) return null;
      const sellingPlan = item.selling_plan_allocation?.selling_plan?.id ?? item.selling_plan ?? '';
      return JSON.stringify([
        String(id), quantity, properties(item.properties), String(sellingPlan)
      ]);
    });
    if (lines.some((line) => line === null)) return null;
    return JSON.stringify([originalTotal, lines.sort()]);
  };

  const contentsSignature = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return null;
    const originalTotal = Number(cart.original_total_price);
    if (!Number.isSafeInteger(originalTotal) || originalTotal < 0) return null;
    const lines = cart.items.map((item) => {
      const id = item.variant_id ?? item.id;
      const quantity = Number(item.quantity);
      if (id == null || !Number.isSafeInteger(quantity) || quantity < 1) return null;
      return JSON.stringify([String(id), quantity]);
    });
    if (lines.some((line) => line === null)) return null;
    return JSON.stringify([originalTotal, lines.sort()]);
  };

  const renderAll = () => instances.forEach((instance) => instance.render());

  const startTimeout = () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      if (pending) {
        timedOut = true;
        renderAll();
      }
    }, timeoutMs);
  };

  const markPending = (waitForCart = false) => {
    epoch += 1;
    pending = true;
    timedOut = false;
    awaitingCart = awaitingCart || waitForCart;
    accepted = null;
    candidate = null;
    startTimeout();
    renderAll();
  };

  const beginMutation = () => {
    mutationVersion += 1;
    freshRawSignatures.clear();
    mutationsInFlight += 1;
    markPending(true);
    let finished = false;
    return () => {
      if (finished) return;
      finished = true;
      mutationsInFlight = Math.max(0, mutationsInFlight - 1);
      mutationVersion += 1;
      freshRawSignatures.clear();
      markPending(true);
    };
  };

  const tryAccept = () => {
    if (awaitingCart || !candidate || candidate.epoch !== epoch || candidate.signature !== expectedSignature) return;
    accepted = candidate;
    pending = false;
    timedOut = false;
    clearTimeout(timeoutId);
    renderAll();
  };

  const setExpectedCart = (cart) => {
    const nextSignature = signature(cart);
    if (nextSignature === null) return false;
    if (nextSignature !== expectedSignature && !pending) markPending();
    expectedCart = cart;
    expectedSignature = nextSignature;
    awaitingCart = false;
    tryAccept();
    renderAll();
    return true;
  };

  const computed = (cart, context) => {
    if (!context || context.epoch !== epoch || mutationsInFlight > 0 ||
        context.mutationVersion !== mutationVersion || !freshRawSignatures.has(context.signature)) return;
    const nextSignature = contentsSignature(cart);
    const amount = Number(cart?.items_subtotal_price);
    if (nextSignature === null || nextSignature !== context.contentsSignature ||
        !Number.isSafeInteger(amount) || amount < 0) {
      return;
    }
    setExpectedCart(context.cart);
    candidate = { epoch: context.epoch, signature: context.signature, amount };
    tryAccept();
  };

  const wrap = (name, before, after) => {
    const original = window[name];
    if (typeof original !== 'function' || original.g2000ShippingProgressHook) return;
    const hooked = function (...args) {
      if (before) before(...args);
      const result = original.apply(this, args);
      if (after) after(...args);
      return result;
    };
    Object.defineProperty(hooked, 'g2000ShippingProgressHook', { value: true });
    window[name] = hooked;
  };

  const isCartMutation = (input, method = 'GET') => {
    if (String(method).toUpperCase() === 'GET') return false;
    try {
      const origin = window.location?.origin || 'https://g2000.com.sg';
      const url = new URL(input, origin);
      return url.origin === origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname);
    } catch {
      return false;
    }
  };

  const aiodPayload = (request, body) => {
    if (!request || String(request.method).toUpperCase() !== 'POST' ||
        typeof body !== 'string' || !window.csapps?.vd_url) return null;
    try {
      const url = new URL(request.url, window.location?.origin);
      const expected = new URL(`${window.csapps.vd_url}/api/cart-request${window.csapps.call_by_dev || ''}`);
      if (url.origin !== expected.origin || url.pathname !== expected.pathname ||
          url.searchParams.get('csapp') !== 'aiod') return null;
      const payload = JSON.parse(body);
      if (payload.discount_cart_load !== true || signature(payload.cdata) === null) return null;
      return payload;
    } catch {
      return null;
    }
  };

  const isRawCartRead = (request) => {
    if (!request || String(request.method).toUpperCase() !== 'GET') return false;
    try {
      const origin = window.location?.origin || 'https://g2000.com.sg';
      const url = new URL(request.url, origin);
      return url.origin === origin && /\/cart\.js$/.test(url.pathname);
    } catch {
      return false;
    }
  };

  const responseJSON = (request) => request.responseType === 'json'
    ? request.response : JSON.parse(request.responseText);

  const installNetworkHooks = () => {
    const fetch = window.fetch;
    if (typeof fetch === 'function' && !fetch.g2000ShippingProgressHook) {
      const hookedFetch = function (input, options) {
        const finish = isCartMutation(typeof input === 'string' ? input : input?.url,
          options?.method || input?.method || 'GET') ? beginMutation() : null;
        try {
          const result = fetch.apply(this, arguments);
          if (finish) Promise.resolve(result).then(finish, finish);
          return result;
        } catch (error) {
          if (finish) finish();
          throw error;
        }
      };
      Object.defineProperty(hookedFetch, 'g2000ShippingProgressHook', { value: true });
      window.fetch = hookedFetch;
    }
    const prototype = window.XMLHttpRequest?.prototype;
    if (!prototype || prototype.send.g2000ShippingProgressHook) return;
    const open = prototype.open;
    const send = prototype.send;
    prototype.open = function (method, url, ...args) {
      xhrRequests.set(this, { method, url });
      handledRequests.delete(this);
      return open.call(this, method, url, ...args);
    };
    const hookedSend = function (...args) {
      if (handledRequests.has(this)) return send.apply(this, args);
      handledRequests.add(this);
      const request = xhrRequests.get(this);
      const finish = request && isCartMutation(request.url, request.method) ? beginMutation() : null;
      if (finish) {
        const stateChanged = () => {
          if (this.readyState !== 4) return;
          this.removeEventListener('readystatechange', stateChanged, true);
          finish();
        };
        this.addEventListener('readystatechange', stateChanged, true);
        ['loadend', 'error', 'abort', 'timeout'].forEach((event) => {
          this.addEventListener(event, finish, { once: true });
        });
      }
      if (isRawCartRead(request)) {
        const readVersion = mutationVersion;
        const startedDuringMutation = mutationsInFlight > 0;
        this.addEventListener('load', () => {
          if (startedDuringMutation || mutationsInFlight > 0 || readVersion !== mutationVersion ||
              this.status < 200 || this.status >= 300) return;
          try {
            const cartSignature = signature(responseJSON(this));
            if (cartSignature !== null) freshRawSignatures.add(cartSignature);
          } catch {
            return;
          }
        }, { capture: true, once: true });
      }
      const payload = aiodPayload(request, args[0]);
      if (payload) {
        markPending();
        const context = { epoch, mutationVersion, cart: payload.cdata, signature: signature(payload.cdata), contentsSignature: contentsSignature(payload.cdata) };
        this.addEventListener('load', () => {
          if (this.status < 200 || this.status >= 300) return;
          try {
            const response = responseJSON(this);
            computed(response?.cdata, context);
          } catch {
            return;
          }
        }, { capture: true, once: true });
      }
      try {
        return send.apply(this, args);
      } catch (error) {
        if (finish) finish();
        throw error;
      }
    };
    Object.defineProperty(hookedSend, 'g2000ShippingProgressHook', { value: true });
    prototype.send = hookedSend;
  };

  const installHooks = () => {
    installNetworkHooks();
    wrap('csapp_checkout_enable', (enabled) => {
      if (Number(enabled) === 0) markPending();
    });
  };

  class G2000ShippingProgress extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.initialise();
    }

    disconnectedCallback() {
      this.connected = false;
      instances.delete(this);
    }

    initialise() {
      this.initialiseAttempts = (this.initialiseAttempts || 0) + 1;
      const configNode = this.querySelector('script[data-config]');
      if (!configNode) {
        this.hidden = true;
        if (this.initialiseAttempts < 3) Promise.resolve().then(() => {
          if (this.connected && !this.config) this.initialise();
        });
        return;
      }
      try {
        this.config = JSON.parse(configNode.textContent);
      } catch {
        this.hidden = true;
        return;
      }
      this.strings = this.config.strings || {};
      this.inScope = this.config.country === 'SG' && this.config.currency === 'SGD';
      this.standardThreshold = Number(this.dataset.standardThreshold || 7900);
      this.expressThreshold = Number(this.dataset.expressThreshold || 25000);
      if (!Number.isSafeInteger(this.standardThreshold) || this.standardThreshold <= 0 ||
          !Number.isSafeInteger(this.expressThreshold) || this.expressThreshold < this.standardThreshold) {
        this.inScope = false;
      }
      instances.add(this);
      const initialSignature = signature(this.config.cart);
      if (initialSignature !== null && expectedCart === null) {
        setExpectedCart(this.config.cart);
        if (mutationVersion === 0 && mutationsInFlight === 0) freshRawSignatures.add(initialSignature);
      }
      installHooks();
      if (pending && timeoutId === undefined) startTimeout();
      this.render();
    }

    message(key, amount) {
      return String(this.strings[key] || '').replaceAll('{amount}', amount || '');
    }

    render() {
      if (!this.config) return;
      const cart = expectedCart || this.config.cart;
      const count = Number(cart?.item_count ?? cart?.items?.reduce((sum, item) => sum + Number(item.quantity || 0), 0));
      this.hidden = !this.inScope || !Number.isFinite(count) || count <= 0;
      if (this.hidden) return;
      const message = this.querySelector('[data-shipping-message]');
      const note = this.querySelector('[data-shipping-note]');
      const progress = this.querySelector('[data-shipping-progress]');
      const visual = this.querySelector('[data-shipping-visual]');
      const currentAmount = this.querySelector('[data-shipping-current-amount]');
      const goal = this.querySelector('[data-shipping-goal]');
      const benefit = this.querySelector('[data-shipping-benefit]');
      const ready = !pending && accepted?.epoch === epoch && accepted?.signature === expectedSignature;
      this.dataset.state = ready ? 'ready' : timedOut ? 'unavailable' : 'pending';
      if (visual) visual.hidden = !ready;
      if (progress) progress.hidden = !ready;
      if (!ready) {
        const text = this.message(timedOut ? 'eligibility_at_checkout' : 'pending');
        delete this.dataset.amount;
        delete this.dataset.source;
        delete this.dataset.activeTier;
        delete this.dataset.complete;
        this.style.setProperty('--shipping-progress', '0%');
        if (message) message.textContent = text;
        if (progress) {
          progress.value = 0;
          progress.setAttribute('aria-valuetext', text);
        }
        if (currentAmount) currentAmount.textContent = '';
        if (goal) goal.textContent = '';
        if (benefit) benefit.textContent = timedOut ? '' : this.message('eligibility_at_checkout');
        if (note) note.textContent = '';
        return;
      }
      const amount = accepted.amount;
      const standardGoal = amount < this.standardThreshold;
      const target = standardGoal ? this.standardThreshold : this.expressThreshold;
      const complete = amount >= this.expressThreshold;
      this.dataset.amount = String(amount);
      this.dataset.source = 'aiod';
      this.dataset.activeTier = standardGoal ? 'standard' : 'express';
      this.dataset.complete = String(complete);
      this.style.setProperty('--shipping-progress', `${Math.min(amount / target, 1) * 100}%`);
      const money = (cents) => new Intl.NumberFormat('en-SG', {
        style: 'currency', currency: 'SGD', currencyDisplay: 'narrowSymbol'
      }).format(cents / 100);
      const text = standardGoal
        ? this.message('standard_remaining', money(this.standardThreshold - amount))
        : !complete
          ? this.message('express_remaining', money(this.expressThreshold - amount))
          : this.message('express_unlocked');
      if (message) message.textContent = text;
      if (progress) {
        progress.max = target;
        progress.value = Math.min(amount, target);
        progress.setAttribute('aria-label', this.message(standardGoal ? 'standard_progress_label' : 'progress_label'));
        progress.setAttribute('aria-valuetext', `${text} ${this.message('cart_total', money(amount))}`);
      }
      if (currentAmount) currentAmount.textContent = this.message('cart_total', money(amount));
      if (goal) goal.textContent = this.message('goal_total', money(target));
      if (benefit) benefit.textContent = this.message(standardGoal ? 'standard_stage_detail' : complete ? 'express_success_detail' : 'express_stage_detail');
      if (note) note.textContent = this.message('after_discounts');
    }
  }

  if (!customElements.get('g2000-shipping-progress')) {
    customElements.define('g2000-shipping-progress', G2000ShippingProgress);
  }

  document.addEventListener('cart:prepare-bundled-sections', () => markPending(true));
  document.addEventListener('line-item:will-change', () => markPending(true));
  document.addEventListener('cart:refresh', () => markPending());
  document.addEventListener('cart:change', (event) => {
    const cart = event.detail?.cart;
    const nextSignature = signature(cart);
    if (nextSignature !== null) {
      if (nextSignature !== expectedSignature) setExpectedCart(cart);
      else if (!pending) renderAll();
    }
    installHooks();
  });
  document.addEventListener('cart-drawer:refreshed', () => {
    installHooks();
    renderAll();
  });
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('g2000-shipping-progress').forEach((element) => {
      if (!element.config) element.initialise();
    });
    installHooks();
  }, { once: true });
  window.addEventListener('load', installHooks, { once: true });
  installHooks();
  let attempts = 0;
  const hookTimer = setInterval(() => {
    installHooks();
    if (++attempts >= 150) clearInterval(hookTimer);
  }, 100);
})();
